import { useEffect, useState } from 'react';
import { useAuthRequest } from 'expo-auth-session';
import { useAuthState } from '../auth/authContext';
import * as WebBrowser from 'expo-web-browser';
import getEnvVars from '../../environments';
import Monitor, { FingerPrint } from '../lib/monitoring/Monitor';
import { fetchTokenAsync, getValidTokenAsync } from '../api/spotifyLamdaRoutes';
import * as LocalStorage from '../lib/LocalStorage';

const { clientId, redirectUri } = getEnvVars();
const discovery = {
  authorizationEndpoint: 'https://accounts.spotify.com/authorize',
  tokenEndpoint: 'https://accounts.spotify.com/api/token',
};
const scopes = [
  'user-modify-playback-state',
  'user-library-modify',
  'user-library-read',
  'user-read-recently-played',
  'user-top-read',
  'user-follow-modify',
  'streaming',
  'user-read-email',
  'user-read-private',
  'user-follow-read',
];

const REDIRECT_URI = redirectUri;

const CLIENT_ID = clientId;

WebBrowser.maybeCompleteAuthSession();

export default function useSpotifyAuth() {
  const { cognitoId, email, userType } = useAuthState();
  const [error, setError] = useState<boolean>(false);
  const [isAuthenticatedViaSpotify, setIsAuthenticated] = useState<boolean>(
    false,
  );
  const [authRequest, authResponse, promptAsync] = useAuthRequest(
    {
      responseType: 'code',
      clientId: CLIENT_ID,
      usePKCE: false,
      scopes,
      redirectUri: REDIRECT_URI,
      extraParams: {
        // On Android it will just skip right past sign in otherwise
        show_dialog: 'true',
      },
    },
    discovery,
  );

  const checkAndRenewAuth = async () => {
    try {
      await getValidTokenAsync();
      setIsAuthenticated(true);
      setError(false);
    } catch (err) {
      const errorString = 'User cannot be authorized via spotify';
      console.error(errorString, err);
      Monitor.notify(
        err,
        setMonitorError(errorString, 'checkAndRenewAuth handler'),
      );
      setError(true);
    }
  };

  const setMonitorError = (message: string, name) => {
    return {
      context: { user_id: cognitoId, user_type: userType, user_email: email },
      name: `useSpotifyAuth-${name}`,
      fingerprint: FingerPrint.fanListen,
      message,
    };
  };

  useEffect(() => {
    async function updateFromAuthResponseAsync() {
      if (authResponse === null) {
        return;
      } else if (authResponse.type === 'error') {
        Monitor.notify(
          authResponse.error,
          setMonitorError(
            'Error in updateFromAuthResponseAsync',
            'updateFromAuthResponseAsync handler',
          ),
        );
        setError(true);
        return;
      } else if (authResponse.type === 'success') {
        // setAuthLoading(true);
        const { params } = authResponse;
        const { code } = params;
        const result = await fetchTokenAsync(code, REDIRECT_URI);
        // TODO: PUT A TRY CATCH HERE
        if (result.error || !result.token) {
          const errorString = 'Fetch token Async error';
          const error = result.error || 'No error object, unkown error';
          console.error(`${errorString} :`, result.error);
          Monitor.notify(
            error,
            setMonitorError(errorString, 'fetchTokenAsync handler'),
          );
          setError(true);
        } else {
          // TODO: Add the show loader screen here: https://app.clubhouse.io/tone-birds/story/309/fan-enable-loading-screen-after-user-has-submitted-to-connect-to-spotify-account
          await LocalStorage.setAuthCredentialsAsync({ ...result });
          setIsAuthenticated(true);
          setError(false);
        }
      }
    }

    if (!isAuthenticatedViaSpotify) {
      updateFromAuthResponseAsync();
    }
  }, [authResponse]);

  return {
    error,
    isAuthenticatedViaSpotify,
    authenticateAsync: () => promptAsync({ useProxy: false }),
    renewAuth: () => checkAndRenewAuth(),
  };
}
